import { useState, useMemo } from 'react';
import { 
  ShieldCheck, Search, FileText, BarChart3, Zap, 
  ClipboardList, FileType, Loader2, Plus, HelpCircle, Trash2, AlertTriangle,
  Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { createFileRoute } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Route Categories (aka prefix) for api routes used in dropdown select list when adding a new routepermission
// Only public API prefixes (served under /api/v1/public/) are shown here
const CATEGORY_PREFIXES: Record<string, string> = {
  'REPORTS': '/reports',
  'TEMPLATES': '/templates',
  'OVERRIDES': '/overrides',
};

export const Route = createFileRoute('/key-management/route-permissions')({
  component: RoutePermissionsTab,
});

interface RoutePermission {
  path: string;
  method: string;
  category: string;
  isPublic: boolean;
  description?: string;
}

export function RoutePermissionsTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMethod, setSelectedMethod] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<RoutePermission | null>(null);
  
  // Track the original identity to handle ID changes (path/method)
  const [editingRouteIdentity, setEditingRouteIdentity] = useState<{ path: string, method: string } | null>(null);

  const [newRoute, setNewRoute] = useState({
    prefix: '', 
    pathSuffix: '', 
    method: '', 
    category: '', 
    description: '',
    isPublic: false
  });

  const fullPath = `${newRoute.prefix}/${newRoute.pathSuffix.replace(/^\//, '')}`;
  const publicPathPreview = `/api/v1/public${fullPath}`;

  const { data: routes = [], isLoading } = useQuery<RoutePermission[]>({
    queryKey: ['api-permissions'],
    queryFn: async () => {
      const response = await api.apiKeyManagement.getRoutePermissions();
      if (response && typeof response === 'object' && 'data' in response) {
        return response.data as RoutePermission[];
      }
      return Array.isArray(response) ? response : [];
    }
  });

  const mutation = useMutation({
    mutationFn: (updatedRoute: RoutePermission) => 
      api.apiKeyManagement.updateRoutePermission(updatedRoute),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-permissions'] });
      toast({ title: "Success", description: "Gateway permissions updated." });
    },
    onError: () => {
      toast({ variant: "destructive", title: "Action Failed", description: "Could not save changes." });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (params: { path: string; method: string }) => 
      api.apiKeyManagement.deleteRoutePermission(params.path, params.method),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-permissions'] });
      setRouteToDelete(null);
    }
  });

  const togglePublicAccess = (route: RoutePermission) => {
    mutation.mutate({ ...route, isPublic: !route.isPublic });
  };

  const handleCategoryChange = (val: string) => {
    setNewRoute(prev => ({
      ...prev,
      category: val,
      prefix: CATEGORY_PREFIXES[val] || '',
    }));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRouteIdentity(null);
    setNewRoute({ prefix: '', pathSuffix: '', method: '', category: '', description: '', isPublic: false });
  };

  const startEditing = (route: RoutePermission) => {
    const prefix = CATEGORY_PREFIXES[route.category] || '';
    // Strip prefix from path to get suffix for the input box
    const suffix = prefix ? route.path.replace(prefix, '').replace(/^\//, '') : route.path;

    setEditingRouteIdentity({ path: route.path, method: route.method });
    setNewRoute({
      category: route.category,
      prefix: prefix,
      pathSuffix: suffix,
      method: route.method,
      description: route.description || '',
      isPublic: route.isPublic
    });
    setIsModalOpen(true);
  };

  const handleAddOrUpdateRoute = async () => {
    if (!newRoute.category || !newRoute.method || !newRoute.pathSuffix) {
      toast({ 
        variant: "destructive", 
        title: "Missing Fields", 
        description: "Category, Method, and Route Path are all required." 
      });
      return;
    }

    // If editing and the identity (Path or Method) changed, delete the old record first
    if (editingRouteIdentity) {
      const identityChanged = editingRouteIdentity.path !== fullPath || editingRouteIdentity.method !== newRoute.method;
      if (identityChanged) {
        await deleteMutation.mutateAsync({ 
          path: editingRouteIdentity.path, 
          method: editingRouteIdentity.method 
        });
      }
    }

    mutation.mutate({
      path: fullPath,
      method: newRoute.method,
      category: newRoute.category,
      isPublic: newRoute.isPublic,
      description: newRoute.description
    });
    
    handleCloseModal();
  };

  const categoriesList = useMemo(() => ['All', ...new Set(routes.map(r => r.category))], [routes]);
  const methodsList = useMemo(() => ['All', ...new Set(routes.map(r => r.method))], [routes]);

  const filteredRoutes = routes.filter(route => {
    const matchesSearch = route.path.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (route.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesCategory = selectedCategory === 'All' || route.category === selectedCategory;
    const matchesMethod = selectedMethod === 'All' || route.method === selectedMethod; 
    return matchesSearch && matchesCategory && matchesMethod;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'TEMPLATES': return <Zap className='w-4 h-4' />;
      case 'CREDIT NOVA REPORT': return <ClipboardList className='w-4 h-4' />; 
      case 'DOCUMENTS': return <FileText className='w-4 h-4' />;
      case 'ANALYSES': return <BarChart3 className='w-4 h-4' />;
      case 'PDF': return <FileType className='w-4 h-4' />;
      default: return <ShieldCheck className="w-4 h-4" />;
    }
  };

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center bg-muted/30 p-4 rounded-xl border border-border">
        <div className="flex items-start gap-2">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Public API Gateway Control
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-primary transition-colors focus:outline-none"><HelpCircle className="w-4 h-4" /></button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-popover text-popover-foreground border-border shadow-xl">
                    <p className="text-xs">Registered routes appear in the Public Gateway. Toggle to <strong>Public</strong> to allow access via external API Keys.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h2>
            <p className="text-sm text-muted-foreground">Define and toggle public accessibility for API endpoints.</p>
          </div>
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <Dialog 
            open={isModalOpen} 
            onOpenChange={(open) => {
              if (!open) {
                handleCloseModal();
              } else {
                setIsModalOpen(true);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button 
                className="gap-2 shadow-sm" 
                onClick={() => setIsModalOpen(true)}
              >
                <Plus className="w-4 h-4" /> Add RoutePermission
              </Button>
            </DialogTrigger>
            
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingRouteIdentity ? 'Edit Route Permission' : 'Register API Endpoint'}
                </DialogTitle>
                <DialogDescription>
                  {editingRouteIdentity 
                    ? 'Modify category, method, or path suffix for this endpoint.' 
                    : 'Select a category to auto-fill the prefix, then type the endpoint path.'}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label className="after:content-['*'] after:ml-0.5 after:text-red-500">Category</Label>
                  <select 
                    className="w-full border rounded-md p-2 text-sm bg-background cursor-pointer" 
                    value={newRoute.category} 
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <option value="" disabled>Select Category</option>
                    {Object.keys(CATEGORY_PREFIXES).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="after:content-['*'] after:ml-0.5 after:text-red-500">Method</Label>
                    <select 
                      className="w-full border rounded-md p-2 text-sm bg-background cursor-pointer" 
                      value={newRoute.method} 
                      onChange={(e) => setNewRoute({...newRoute, method: e.target.value})}
                    >
                      <option value="" disabled>Select</option>
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Route Prefix</Label>
                    <Input 
                      value={newRoute.prefix} 
                      readOnly 
                      className="bg-muted font-mono text-xs cursor-not-allowed" 
                      placeholder="/prefix" 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="after:content-['*'] after:ml-0.5 after:text-red-500">Route Path</Label>
                  <Input 
                    value={newRoute.pathSuffix} 
                    onChange={(e) => setNewRoute({...newRoute, pathSuffix: e.target.value})} 
                    placeholder="e.g. summary-async"
                    className="font-mono"
                  />
                </div>

                <div className="p-3 bg-muted rounded-md border border-dashed border-primary/30">
                  <Label className="text-[10px] uppercase text-primary font-bold">Public Gateway Preview</Label>
                  <div className="text-xs font-mono break-all text-muted-foreground mt-1">
                    {newRoute.category && newRoute.pathSuffix 
                      ? publicPathPreview 
                      : <span className="italic opacity-50">Awaiting selection...</span>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input 
                    value={newRoute.description} 
                    onChange={(e) => setNewRoute({...newRoute, description: e.target.value})} 
                    placeholder="Internal endpoint description" 
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={handleCloseModal}>Cancel</Button>
                <Button onClick={handleAddOrUpdateRoute}>
                  {editingRouteIdentity ? 'Update' : 'Save'} Route
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input type="text" placeholder="Search..." className="pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-primary/50" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
      </div>

      <Dialog open={!!routeToDelete} onOpenChange={(open) => !open && setRouteToDelete(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Delete Route Permission?</DialogTitle>
            <DialogDescription className="pt-2">This will remove <code className="text-foreground font-bold">{routeToDelete?.path}</code> ({routeToDelete?.method}) from the gateway control.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setRouteToDelete(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => routeToDelete && deleteMutation.mutate({ path: routeToDelete.path, method: routeToDelete.method })}>{deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirm Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border border-border rounded-xl overflow-hidden shadow-sm bg-card">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="p-4 font-semibold w-px">
                <div className="flex flex-col gap-2">
                  <span className="text-foreground">Method</span>
                  <select className="bg-background border border-border rounded px-1 py-1 text-[12px] font-normal focus:ring-1 focus:ring-primary outline-none cursor-pointer" value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value)}>{methodsList.map(m => <option key={m} value={m}>{m}</option>)}</select>
                </div>
              </th>
              <th className="p-4 font-semibold w-px">
                <div className="flex flex-col gap-2"><span className="text-foreground">Endpoint</span><div className="h-[22px]" aria-hidden="true" /></div>
              </th>
              <th className="p-4 font-semibold w-48">
                <div className="flex flex-col gap-2">
                  <span className="text-foreground">Category</span>
                  <select className="bg-background border border-border rounded px-1 py-1 text-[12px] font-normal focus:ring-1 focus:ring-primary outline-none cursor-pointer" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>{categoriesList.map(c => <option key={c} value={c}>{c}</option>)}</select>
                </div>
              </th>
              <th className="p-4 font-semibold"><div className="flex flex-col gap-2"><span className="text-foreground">Description</span><div className="h-[22px]" aria-hidden="true" /></div></th>
              <th className="p-4 font-semibold w-32"><div className="flex flex-col gap-2 text-center"><span className="text-foreground">Status</span><div className="h-[22px]" aria-hidden="true" /></div></th>
              <th className="p-4 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredRoutes.map((route) => {
              const routeId = `${route.method}-${route.path}`;
              const isUpdating = mutation.isPending && mutation.variables?.path === route.path && mutation.variables?.method === route.method;
              return (
                <tr key={routeId} className="hover:bg-muted/10 transition-colors group">
                  <td className="p-4"><span className={`px-2 py-0.5 rounded text-[12px] font-bold border ${route.method === 'GET' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : route.method === 'POST' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-orange-500/10 text-orange-500 border-orange-500/20'}`}>{route.method}</span></td>
                  <td className="p-4 whitespace-nowrap"><code className="text-s font-mono font-semibold text-primary bg-primary/5 px-1.5 py-0.5 rounded">{route.path}</code></td>
                  <td className="p-4"><div className="flex items-center gap-2 text-muted-foreground">{getCategoryIcon(route.category)}{route.category}</div></td>
                  <td className="p-4 text-muted-foreground italic truncate max-w-[300px]">{route.description || 'No description provided'}</td>
                  <td className="p-4"><div className="flex justify-center gap-2 items-center"><button type="button" disabled={isUpdating} onClick={() => togglePublicAccess(route)} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all focus:ring-2 focus:ring-primary ${route.isPublic ? 'bg-primary' : 'bg-muted-foreground/30'} ${isUpdating ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}><span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${route.isPublic ? 'translate-x-5.5' : 'translate-x-1'}`} /></button><span className="text-[10px] font-bold uppercase w-12">{route.isPublic ? 'Public' : 'Private'}</span></div></td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => startEditing(route)} className="p-2 text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setRouteToDelete(route)} className="p-2 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}